import React from "react";
import Main from "layouts/Main";
import { createDataset, getOrganizationsByAdminId, getOrganizationUsers, modifyDataset, updateDataset } from "services/api";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputLabel,
	ListItemText,
	ListSubheader,
	MenuItem,
	OutlinedInput,
	Radio,
	RadioGroup,
	Select,
	Slider,
	TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Dataset } from "types/dataset";
import { useTranslation } from "react-i18next";
import Loading from "components/Loading";
import Container from "components/Container";
import { AccountContext } from "services/account";
import { Organization, OrganizationUser } from "types/organization";
import { showSnackbar } from "services/snackbarHelper";

const EditDataset = () => {
	const { id } = useParams();
	const { t } = useTranslation(["common"]);
	const navigate = useNavigate();
	const account = React.useContext(AccountContext);

	const [organizations, setOrganizations] = React.useState<Organization[]>([]);
	const organizationMap = React.useMemo(
		() => new Map(organizations.map((organizations) => [organizations.organizationId, organizations])),
		[organizations]
	);

	const [users, setUsers] = React.useState<{ [k: string]: OrganizationUser[] }>({});
	const userMap = React.useMemo(
		() =>
			new Map(
				Object.values(users)
					.flat()
					.map((user) => [user.userId, user])
			),
		[users]
	);

	const [dataset, setDataset] = React.useState<Dataset>({
		name: "",
		description: "",
		mode: "private",
		ownedBy: "",
		id: "",
		clarificationPrompt: "",
		ragThreshold: 0.5,
		creditToUse: 0,
		userCanUse: [],
	});

	const [isLoading, setLoading] = React.useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		console.log(name, value);
		if (name === "ragThreshold") {
			const newValue = Math.round(value * 100) / 100;
			setDataset({ ...dataset, [name]: newValue });
		} else {
			setDataset({ ...dataset, [name]: value });
		}
	};

	const handleSliderChange = (event, newValue: number | number[]) => {
		const { name } = event.target;
		setDataset({ ...dataset, [name]: newValue as number });
	};

	const handleCreateDataset = () => {
		if (id) {
			updateDataset(id, dataset).then((resp) => {
				showSnackbar(t("updateSuccess", { ns: "validation" }), "success");
				navigate("/app/dataset/list");
			});
		} else {
			createDataset(dataset).then((resp) => {
				setDataset({ ...dataset, id: resp.dataset });
				navigate("/app/document/add/" + resp.dataset);
			});
		}
	};

	React.useEffect(() => {
		if (id) {
			setLoading(true);
			modifyDataset(id, "getInfo", {}).then((resp) => {
				if (!resp || resp?.error) return;
				setDataset({ ownedBy: account.value.userId, ...resp });
				setLoading(false);
			});
		}
	}, []);
	React.useEffect(() => {
		if (account.checkPremission(["organizationAdmin"])) {
			const getData = async () => {
				const organizations = await getOrganizationsByAdminId();
				const userList = await Promise.all(organizations.map((organization) => getOrganizationUsers(organization.organizationId)));
				setOrganizations(organizations);

				setUsers(
					Array.from(userList.entries()).reduce((acc, [key, value]) => {
						acc[organizations[key].organizationName] = value;
						return acc;
					}, {})
				);
			};
			getData();
		}
	}, []);
	Object.entries(users).map(([organizationName, organizationUsers]) =>{
		console.log(organizationName, organizationUsers);
	});

	return (
		<Main title={id ? t("editDataset") : t("createDataset")}>
			{isLoading ? (
				<Loading />
			) : (
				<Container>
					<TextField
						fullWidth
						label={t("documentName") + "*"}
						name="name"
						value={dataset.name}
						onChange={handleChange}
						margin="normal"
					/>
					<TextField
						fullWidth
						label={t("documentDescription")}
						name="description"
						value={dataset.description}
						onChange={handleChange}
						margin="normal"
					/>
					{account.checkPremission(["organizationAdmin"]) && (
						<>
							<FormControl
								fullWidth
								sx={{ mt: 2, mb: 1 }}>
								<InputLabel>{t("datasetOwner") + "*"}</InputLabel>
								<Select
									name={"ownedBy"}
									value={dataset.ownedBy}
									onChange={(e) => {
										handleChange(e);
									}}
									input={<OutlinedInput label={t("datasetOwner") + "*"} />}
									MenuProps={{
										PaperProps: {
											style: {
												width: 250,
											},
										},
									}}>
									<MenuItem value={account.value.userId}>{t("organizationAdmin")}</MenuItem>
									{organizations.map((organization) => (
										<MenuItem
											key={organization.organizationId}
											value={organization.organizationId}>
											<ListItemText primary={organization.organizationName} />
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl
								fullWidth
								sx={{ mt: 2, mb: 1 }}>
								<InputLabel>{t("userCanUse")}</InputLabel>
								<Select
									multiple
									name={"userCanUse"}
									value={dataset.userCanUse}
									onChange={(e) => {
										console.log(e.target);
										if (e.target.value.includes("all")) {
											const allUserIds = Array.from(userMap.keys());
											const newValue = dataset.userCanUse.length === Array(userMap).length ? [] : allUserIds;
											handleChange({ ...e, target: { ...e.target, value: newValue } });
										} else {
											handleChange(e);
										}
									}}
									input={<OutlinedInput label={t("userCanUse")} />}
									renderValue={(selected) =>
										selected
											.map((item) => userMap.get(item)?.username)
											.filter(Boolean)
											.join(", ")
									}
									MenuProps={{
										PaperProps: {
											style: {
												width: 250,
											},
										},
									}}>
									<MenuItem value="all">
										<Checkbox
											checked={dataset.userCanUse.length === Array.from(userMap).length}
											indeterminate={dataset.userCanUse.length > 0 && dataset.userCanUse.length < Array.from(userMap).length}
										/>
										<ListItemText primary={t("all")} />
									</MenuItem>
									
									{Object.entries(users).map(([organizationName, organizationUsers]) =>
										
										[<ListSubheader>{organizationName}</ListSubheader>].concat(
											organizationUsers.length === 0 ? (
												<MenuItem disabled>{t("noUserInOrganization")}</MenuItem>
											) : (
											organizationUsers.map((user) => (
												<MenuItem
													key={user.userId}
													value={user.userId}>
													<Checkbox checked={dataset.userCanUse.includes(user.userId)} />
													<ListItemText primary={user.username} />
												</MenuItem>
											))
										)
									)
									)}
								</Select>
							</FormControl>
						</>
					)}

					<TextField
						fullWidth
						label={t("clarificationPrompt")}
						name="clarificationPrompt"
						value={dataset.clarificationPrompt}
						onChange={handleChange}
						margin="normal"
						multiline
						minRows={3}
					/>

					<FormControl>
						<FormLabel>{t("ragThreshold")}</FormLabel>
						<Box
							width={350}
							display="flex"
							flexDirection={"row"}
							gap={2}>
							<Slider
								name="ragThreshold"
								value={typeof dataset.ragThreshold === "number" ? dataset.ragThreshold : 0.1}
								onChange={handleSliderChange}
								step={0.01}
								min={0.1}
								max={0.9}
							/>
							<OutlinedInput
								name="ragThreshold"
								value={dataset.ragThreshold}
								size="small"
								onChange={handleChange}
								onBlur={() => {
									if (dataset.ragThreshold < 0.1) {
										setDataset((prev) => {
											return { ...prev, ragThreshold: 0.1 };
										});
									} else if (dataset.ragThreshold > 0.9) {
										setDataset((prev) => {
											return { ...prev, ragThreshold: 0.9 };
										});
									}
								}}
								inputProps={{
									step: 0.01,
									min: 0.1,
									max: 0.9,
									type: "number",
								}}
								sx={{ width: 100 }}
							/>
						</Box>
					</FormControl>

					<FormControl fullWidth>
						<FormLabel>{t("mode")}</FormLabel>
						<RadioGroup
							row
							value={dataset.mode}
							name="mode"
							onChange={handleChange}>
							<FormControlLabel
								value="private"
								control={<Radio />}
								label={t("private")}
							/>
							<FormControlLabel
								value="public"
								control={<Radio />}
								label={t("public")}
							/>
						</RadioGroup>
					</FormControl>

					<Box
						width={1}
						mt={1}>
						<Button
							variant="contained"
							onClick={handleCreateDataset}>
							{t("submit")}
						</Button>
					</Box>
				</Container>
			)}
		</Main>
	);
};

export default EditDataset;
